/**
 * /data/status.json: per-provider freshness and which optional datasets
 * (latency, validation, timelines) are published. Optional datasets are only
 * requested when they exist, and a missing dataset is remembered so it is not
 * requested again on every page.
 */
const DATA_BASE_URL = import.meta.env.VITE_DATA_URL || '/data';

export type OptionalDataset = 'latency' | 'validation';

export interface DataStatusDataset {
  available: boolean;
  url?: string;
  [key: string]: unknown;
}

export interface DataStatus {
  schemaVersion: string;
  generatedAt: string;
  catalog?: {
    generatedAt?: string;
    oldestProviderCollectedAt?: string;
    status?: string;
  };
  datasets?: Partial<Record<string, DataStatusDataset>>;
}

/** Thrown instead of a network error when a dataset is not published. */
export class DatasetNotPublishedError extends Error {
  constructor(readonly dataset: OptionalDataset) {
    super(`The ${dataset} dataset is not published`);
    this.name = 'DatasetNotPublishedError';
  }
}

export function isDatasetNotPublished(error: unknown): boolean {
  return error instanceof DatasetNotPublishedError;
}

let statusPromise: Promise<DataStatus | null> | null = null;
const missingDatasets = new Set<OptionalDataset>();

export function fetchDataStatus(): Promise<DataStatus | null> {
  if (!statusPromise) {
    statusPromise = fetch(`${DATA_BASE_URL}/status.json`)
      .then(async (response) => {
        if (!response.ok) return null;
        const parsed = (await response.json()) as DataStatus;
        return parsed && typeof parsed === 'object' ? parsed : null;
      })
      .catch(() => null);
  }
  return statusPromise;
}

/**
 * False when status.json says the dataset is not published or a previous
 * request returned 404. True when it is published or status is unavailable.
 */
export async function isDatasetPublished(dataset: OptionalDataset): Promise<boolean> {
  if (missingDatasets.has(dataset)) return false;
  const status = await fetchDataStatus();
  const entry = status?.datasets?.[dataset];
  if (entry && entry.available === false) {
    missingDatasets.add(dataset);
    return false;
  }
  return true;
}

/** Record that a dataset request returned 404 so it is not retried. */
export function markDatasetMissing(dataset: OptionalDataset): void {
  missingDatasets.add(dataset);
}

/** Test helper. */
export function resetDataStatusCache(): void {
  statusPromise = null;
  missingDatasets.clear();
}
